import React from "react";
import styles from "../../styles";
import { IDEStyles } from "./style.js";
import CodeEditor from "../CodeEditor";
import FileExplorer from "../FileExplorer";
import Window from "../Window";
import { CloseOutlined } from "@ant-design/icons";
import WebView from "../WebView";
import ResizeBar from "../ResizeBar";
import { evaluate, lString } from "../../utils.js";
import ConsoleEditor from "../ConsoleEditor";

export const DisplayModes = {
  NONE: 0,
  PREVIEW: 1,
  CONSOLE: 2,
};

class IDE extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      currentFiles: [],
      currentIndex: 0,
      displayMode: DisplayModes.NONE,
      dimensions: {
        width: 650,
        height: 430,
        minWidth: 650,
        minHeight: 430,
        codeEditorWidth: 400,
      },
      resizing: false,
      tempAppData: {},
    };
    this.openFile = this.openFile.bind(this);
    this.saveFile = this.saveFile.bind(this);
    this.onBeforeChange = this.onBeforeChange.bind(this);
    this.onChange = this.onChange.bind(this);
    this.setFileIndex = this.setFileIndex.bind(this);
    this.changeDisplayMode = this.changeDisplayMode.bind(this);
    this.setDimensions = this.setDimensions.bind(this);
    this.highlight = this.highlight.bind(this);
    this.unhighlight = this.unhighlight.bind(this);
    this.logMessage = this.logMessage.bind(this);
  }

  componentDidMount() {
    window.addEventListener("message", this.logMessage);
  }

  componentWillUnmount() {
    window.removeEventListener("message", this.logMessage);
  }

  logMessage(e) {
    const message = e.data;
    const hash = "ide";
    const frame = document.getElementById(hash + "_appView");
    if (message.type === "exec") {
      console.log("EXEC", hash, message.command);
    } else if (message.type === "post") {
      console.log("POST", hash, message.key, message.value);
      this.setState((state) => ({
        ...state,
        appData: {
          ...state.appData,
          [message.key]: message.value,
        },
      }));
    } else if (message.type === "get") {
      const value = this.state.appData[message.key];
      frame.contentWindow.postMessage({ ...message, response: value });
      console.log(value);
    }
  }

  openFile(file) {
    for (let entry of this.state.currentFiles) {
      if (entry.file.getHash() === file.getHash()) return;
    }
    this.setState((state) => ({
      ...state,
      currentFiles: [
        ...state.currentFiles,
        { file, value: file.getData(), saved: true },
      ],
    }));
  }

  saveFile(index) {
    this.props.fileSystem.saveFile(
      this.state.currentFiles[index].file.getHash(),
      this.state.currentFiles[index].value
    );
    this.setState((state) => {
      const files = this.state.currentFiles;
      files[index].saved = true;
      return {
        ...state,
        currentFiles: files,
      };
    });
  }

  closeFile(index) {
    const files = this.state.currentFiles;
    if (files[index].saved) {
      files.splice(index, 1);
      this.setState((state) => ({
        ...state,
        currentFiles: files,
      }));
    } else {
      const res = window.confirm("Do you want to save your changes?");
      if (res) {
        this.props.fileSystem.saveFile(
          this.state.currentFiles[index].file.getHash(),
          this.state.currentFiles[index].value
        );
      }
      this.setState((state) => {
        files.splice(index, 1);
        return {
          ...state,
          currentFiles: files,
        };
      });
    }
  }

  onBeforeChange = (editor, data, value, index) => {
    this.setState((state) => {
      const files = this.state.currentFiles;
      files[index].value = value;
      files[index].saved = false;
      return {
        ...state,
        currentFiles: files,
      };
    });
  };

  onChange = () => {};

  setFileIndex = (index) => {
    this.setState((state) => ({
      ...state,
      currentIndex: index,
    }));
  };

  changeDisplayMode = (mode) => {
    this.setState((state) => ({
      ...state,
      displayMode: mode,
    }));
  };

  setDimensions = (width, height) => {
    this.setState((state) => ({
      ...state,
      dimensions: {
        ...state.dimensions,
        width,
        height,
      },
    }));
  };

  resizeCodeEditor = (width) => {
    this.setState((state) => ({
      ...state,
      dimensions: {
        ...state.dimensions,
        codeEditorWidth: width,
      },
      resizing: true,
    }));
  };

  highlight(e) {
    e.target.style.opacity = 0.8;
  }

  unhighlight(e) {
    e.target.style.opacity = 1;
  }

  render() {
    const ind = this.state.currentIndex;
    const currentFiles = this.state.currentFiles;
    const displayMode = this.state.displayMode;
    const tabs = (
      <div style={IDEStyles.tabRow}>
        {currentFiles.map((entry, index) => (
          <div
            key={index}
            style={IDEStyles.tabCell}
            onClick={() => this.setFileIndex(index)}
            onMouseOver={this.highlight}
            onMouseOut={this.unhighlight}
          >
            {lString(entry.file.getName(), 10, "...") +
              (entry.saved ? " ●" : " ○")}
            <div onClick={() => this.closeFile(index)}>
              <CloseOutlined />
            </div>
          </div>
        ))}
      </div>
    );
    return (
      <Window
        id={"code_editor_window"}
        name={"JS Code"}
        top={0}
        left={0}
        onCancel={this.props.closeWindow}
        onResize={this.setDimensions}
      >
        {tabs}
        <div
          style={{
            ...styles.row,
            flex: 1,
            minHeight: 0,
          }}
        >
          <FileExplorer
            fileSystem={this.props.fileSystem}
            currentDirectoryHash={this.props.currentHash}
            width={250}
            height={"100%"}
            openFile={this.openFile}
          />
          {currentFiles[ind] && (
            <CodeEditor
              value={currentFiles[ind].value}
              width={this.state.dimensions.codeEditorWidth}
              height={"100%"}
              title={currentFiles[ind].file.getName()}
              id={currentFiles[ind].file.getHash()}
              saved={currentFiles[ind].saved}
              onBeforeChange={(editor, data, value) =>
                this.onBeforeChange(editor, data, value, ind)
              }
              onChange={(editor, data, value) =>
                this.onChange(editor, data, value, ind)
              }
              saveFile={() => this.saveFile(ind)}
              changeDisplayMode={this.changeDisplayMode}
            />
          )}
          {currentFiles[ind] && (
            <ResizeBar
              resizeable
              leftWidth={this.state.dimensions.codeEditorWidth}
              onResize={this.resizeCodeEditor}
              onFinishResize={this.finishResize}
            />
          )}
          {displayMode === DisplayModes.PREVIEW ? (
            currentFiles[ind] && (
              <WebView
                id={"ide_appView"}
                width={Math.max(
                  this.state.dimensions.width -
                    250 -
                    3 -
                    this.state.dimensions.codeEditorWidth,
                  300
                )}
                height={"100%"}
                data={currentFiles[ind].value}
              />
            )
          ) : displayMode === DisplayModes.CONSOLE ? (
            currentFiles[ind] && (
              <ConsoleEditor
                width={Math.max(
                  this.state.dimensions.width -
                    250 -
                    5 -
                    this.state.dimensions.codeEditorWidth,
                  300
                )}
                height={"100%"}
                fileSystem={this.props.fileSystem}
                lines={[{ value: "" }]}
                evalFunc={this.props.systemAPI.executeCommand}
              />
            )
          ) : (
            <></>
          )}
        </div>
      </Window>
    );
  }
}

export default IDE;
